import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { v4 as uuid } from 'uuid';
import { enhanceGLBridge, fixZipResUrl, IEnhanceParams } from '@meitu/wasm-lib';
import assetsConfig from '@meitu/wasm-lib/assetsConfig.json';
import { getFramework, IEdgelessPreviewClearable, IFramework, type IGetSrcImage } from '@/framework';
import { type MainLayer } from '@/layers/main-layer';
import { applyRenderedMainLayerURL } from '@/layers/main-layer/utils';
import { base64Kit } from '@/utils/base64-kit';
import { createCanvas, getImgInfoAsync } from '@/utils/file-kit';
import { glKit } from '@/utils/gl-kit';
import { IImageLayerProps, IImageProps, ILayerProps, LayerType } from '@/version-types';
import { defaultEnhanceParams } from '../config';
import { getImageEnhanceState, IStore, watchImageEnhanceChange } from '../states/image-enhance-state';
import { adapterEnhanceArgs, getHslArgs, getPosterizeArgs } from '../utils';

const assets = {
    id: 'Enhance',
    zip: assetsConfig.filter_Enhance,
};

export class BasicImageEnhance {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;

    // 功能使用记录
    private enhanceUsedList = new Set<string>();
    private customEvents = '';

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getImageEnhanceState(identity);

        watchImageEnhanceChange(identity, (state) => {
            this.store = state;
        });
    }

    getCustomEvents() {
        return this.customEvents;
    }

    private tryGetMainLayer() {
        const mainLayer = this.framework.service.registration.getInstance<MainLayer>('MainLayer');
        if (!mainLayer) {
            throw new Error('MainLayer 实例不存在');
        }
        return mainLayer;
    }

    async create(
        _originLayer: ILayerProps,
        customEvents?: string,
        isBatchEditor?: boolean,
    ): Promise<HTMLCanvasElement | null> {
        const originLayer = await this.tryGetMainLayer().enterMergeMode(_originLayer);
        const newLayer = cloneDeep(originLayer);

        this.store.setOriginLayer(originLayer);

        // 初始化gl数据
        const obj = this.framework.service.edgelessCanvas.getByLayerId(originLayer.id) as unknown as IGetSrcImage;
        if (!obj || !obj.getSrcImage) {
            throw new Error('===>未找到对应的无限画布对象');
        }

        const rawCanvas = await obj.getSrcImage();
        glKit.openGL(newLayer);

        const fromCanvas = glKit.scaleResourceIfNeeded(rawCanvas);
        const glCanvas = await enhanceGLBridge.from(fromCanvas).create(assets.id, fixZipResUrl(assets.zip));

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        this.customEvents = customEvents ?? '';

        return glCanvas;
    }

    private clearEdgelessPreview(layerId: string) {
        const obj = this.framework.service.edgelessCanvas.getByLayerId(
            layerId,
        ) as unknown as IEdgelessPreviewClearable | null;
        obj?.clearPreviewResource?.({ rerender: false });
    }

    private async destroy() {
        const layerId = this.store.originLayer?.id || '';
        const layer = cloneDeep(this.framework.service.flatLayer.getLayer(layerId));

        // HACK: 防御性代码，防止 originLayer 为空
        if (!layer) {
            return;
        }

        glKit.clearGL(layer);
        this.clearEdgelessPreview(layerId);

        this.framework.service.frame.addOrUpdateLayer(layer);
        this.store.resetEnhance();
        this.enhanceUsedList.clear();
        this.tryGetMainLayer().exitMergeMode();
    }

    async batchOneAction(params: IEnhanceParams): Promise<void> {
        return this.action(params);
    }

    /**
     * 实时应用效果，但不修改图层结果和保存到历史记录
     *
     * @param params - 参数
     * @param trackFunc - 方法
     */
    async action(params: IEnhanceParams, trackFunc?: string): Promise<void> {
        const { framework } = this;
        const layerId = this.store.originLayer?.id;
        if (!layerId) return;

        const newLayer = framework.service.flatLayer.getLayer(layerId, true);

        // HACK: 防御性代码，防止 originLayer 为空
        if (!newLayer) {
            return;
        }

        const args = {};
        const baseData = this.store.baseEnhance;
        const colorData = this.store.colorEnhance;
        const lightData = this.store.lightEnhance;
        const particularsData = this.store.particularsEnhance;
        const posterizeData = this.store.posterizeEnhance;
        const hslData = this.store.hslEnhance;

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            params,
        );

        await enhanceGLBridge.setParams(adapterEnhanceArgs(args));

        glKit.processGL(newLayer);

        this.framework.service.frame.addOrUpdateLayer(newLayer);
        if (trackFunc) this.enhanceUsedList.add(trackFunc);
    }

    async batchReset(): Promise<void> {
        for (let i = 0; i < this.store.originLayers.length; i++) {
            const mainLayer = this.store.originLayers[i];
            if (!mainLayer) {
                continue;
            }

            this.framework.service.frame.addOrUpdateLayer(cloneDeep(mainLayer));
        }

        await new Promise((resolve) => {
            setTimeout(async () => {
                const originLayer = this.store.originLayer;
                if (originLayer) {
                    await this.create(originLayer, undefined, true);
                    await this.action({});
                }
                resolve(true);
            }, 10);
        });
    }

    async batchAction(
        params: IEnhanceParams,
        onStageLoading?: (stageIndex: number, isBatch: boolean) => void,
    ): Promise<void> {
        console.time('basic enhance batch action');
        const { framework } = this;
        const args = {};

        const baseData = this.store.baseEnhance;
        const colorData = this.store.colorEnhance;
        const lightData = this.store.lightEnhance;
        const particularsData = this.store.particularsEnhance;
        const posterizeData = this.store.posterizeEnhance;
        const hslData = this.store.hslEnhance;

        merge(
            args,
            baseData,
            lightData,
            colorData,
            particularsData,
            getPosterizeArgs(posterizeData),
            getHslArgs(hslData),
            params,
        );

        for (let i = 1; i < this.store.originLayers.length; i++) {
            try {
                const mainLayer = cloneDeep(this.store.originLayers[i]);

                if (!mainLayer) {
                    continue;
                }

                const { naturalHeight, naturalWidth, imageTransform } = mainLayer.data as IImageLayerProps;
                const { img } = await getImgInfoAsync((mainLayer.data as IImageLayerProps).url);
                const { canvas } = createCanvas(
                    naturalWidth * imageTransform.scale,
                    naturalHeight * imageTransform.scale,
                    img,
                );
                await enhanceGLBridge.from(canvas).create(assets.id, fixZipResUrl(assets.zip));
                await enhanceGLBridge.setParams(adapterEnhanceArgs(args));
                const { canvas: resultCanvas } = await enhanceGLBridge.getGLResult();

                if (resultCanvas) {
                    const base64 = resultCanvas.toDataURL();
                    const data = mainLayer.data as IImageLayerProps;

                    data.url = base64Kit.getCode(base64, uuid());
                }

                framework.service.frame.addOrUpdateLayer(cloneDeep(mainLayer));
                onStageLoading?.(i, false);
            } catch (error) {
                console.error(error);
                onStageLoading?.(i, false);
            }
        }
        console.timeEnd('basic enhance batch action');
    }

    /**
     * 应用效果，并保存到历史记录
     */
    async applyConfirm() {
        const { framework, enhanceUsedList } = this;
        const oldLayer = this.store.originLayer;

        if (!oldLayer) {
            return;
        }

        const layerInFlat = framework.service.flatLayer.getLayer(oldLayer.id);
        const runtimeLayer = framework.service.frame.getRuntimeLayer(oldLayer.id);

        if (!layerInFlat) {
            this.store.resetEnhance();
            return;
        }

        const isGLActive =
            glKit.isGLProcessed(layerInFlat) ||
            glKit.isUseGL(layerInFlat) ||
            (runtimeLayer && glKit.isGLProcessed(runtimeLayer)) ||
            (runtimeLayer && glKit.isUseGL(runtimeLayer));

        if (!isGLActive) {
            this.destroy();
            return;
        }

        const newLayer = cloneDeep(layerInFlat);
        newLayer.__isWebGLProcessed = true;

        const currentParams = {
            baseEnhance: this.store.baseEnhance,
            colorEnhance: this.store.colorEnhance,
            lightEnhance: this.store.lightEnhance,
            particularsEnhance: this.store.particularsEnhance,
            posterizeEnhance: this.store.posterizeEnhance,
            hslEnhance: this.store.hslEnhance,
        };

        // 参数没有变化，则不写入历史记录
        if (JSON.stringify(currentParams) === JSON.stringify(defaultEnhanceParams)) {
            this.destroy();
            return;
        }

        await glKit.applyGLResult({
            layer: newLayer,
            props: newLayer.data as IImageProps,
            baseBridge: enhanceGLBridge,
            resultSize: {
                width: (newLayer.data as IImageProps).naturalWidth,
                height: (newLayer.data as IImageProps).naturalHeight,
            },
        });

        const latestLayer = framework.service.flatLayer.getLayer(newLayer.id, true);
        if (!latestLayer) {
            this.store.resetEnhance();
            this.store.setOriginLayer(null);
            this.enhanceUsedList.clear();
            return;
        }
        const committedLayer = applyRenderedMainLayerURL(latestLayer, (newLayer.data as IImageProps).url);
        framework.service.frame.addOrUpdateLayer(committedLayer);

        const trackFunc = [...enhanceUsedList].join(',');
        const mergeResult = this.tryGetMainLayer().applyMergeMode(committedLayer, trackFunc);

        if (!mergeResult) {
            // 撤销必须恢复旧 URL，不能重新启用已经提交的 GL 预览画布。
            glKit.clearGL(latestLayer);
            framework.service.history.write(latestLayer, committedLayer, trackFunc);
        }

        this.store.resetEnhance();
        this.store.setOriginLayer(null);
        this.enhanceUsedList.clear();
        // 有应用效果则返回 true
        return true;
    }

    async batchApplyConfirm() {
        const currentParams = {
            baseEnhance: this.store.baseEnhance,
            colorEnhance: this.store.colorEnhance,
            lightEnhance: this.store.lightEnhance,
            particularsEnhance: this.store.particularsEnhance,
            posterizeEnhance: this.store.posterizeEnhance,
            hslEnhance: this.store.hslEnhance,
        };

        // 参数没有变化，则不写入历史记录
        if (JSON.stringify(currentParams) === JSON.stringify(defaultEnhanceParams)) {
            this.destroy();
            return;
        }

        const trackFunc = [...this.enhanceUsedList].join(',');
        const currentMainLayers = [] as ILayerProps[];
        const stageList = this.framework.service.frame.getFrames();
        for (let i = 0; i < stageList.length; i++) {
            const mainLayer = stageList[i].find((layer) => layer.layerType === LayerType.MAIN_LAYER);
            if (!mainLayer) {
                continue;
            }
            if (i === 0) {
                const { naturalHeight, naturalWidth, imageTransform } = mainLayer.data as IImageLayerProps;
                const { img } = await getImgInfoAsync((mainLayer.data as IImageLayerProps).url);
                const { canvas } = createCanvas(
                    naturalWidth * imageTransform.scale,
                    naturalHeight * imageTransform.scale,
                    img,
                );
                await enhanceGLBridge.from(canvas).create(assets.id, fixZipResUrl(assets.zip));
                const args = {};

                const baseData = this.store.baseEnhance;
                const colorData = this.store.colorEnhance;
                const lightData = this.store.lightEnhance;
                const particularsData = this.store.particularsEnhance;
                const posterizeData = this.store.posterizeEnhance;
                const hslData = this.store.hslEnhance;

                merge(
                    args,
                    baseData,
                    lightData,
                    colorData,
                    particularsData,
                    getPosterizeArgs(posterizeData),
                    getHslArgs(hslData),
                );
                await enhanceGLBridge.setParams(adapterEnhanceArgs(args));
                const { canvas: resultCanvas } = await enhanceGLBridge.getGLResult();

                if (resultCanvas) {
                    const base64 = resultCanvas.toDataURL();
                    const data = mainLayer.data as IImageLayerProps;

                    data.url = base64Kit.getCode(base64, uuid());
                }

                await glKit.applyGLResult({
                    layer: mainLayer,
                    props: mainLayer.data as IImageProps,
                    baseBridge: enhanceGLBridge,
                    resultSize: {
                        width: (mainLayer.data as IImageProps).naturalWidth,
                        height: (mainLayer.data as IImageProps).naturalHeight,
                    },
                });

                this.framework.service.frame.addOrUpdateLayer(cloneDeep(mainLayer));
            }
            currentMainLayers.push(mainLayer);
        }
        this.framework.service.history.write(
            cloneDeep(this.store.originLayers),
            cloneDeep(currentMainLayers),
            trackFunc,
        );
        this.store.resetEnhance();
        this.store.setOriginLayers(cloneDeep(currentMainLayers));
        this.store.setOriginLayer(null);
        this.enhanceUsedList.clear();
        // 有应用效果则返回 true
        return true;
    }
}
